<template>
  <v-dialog
    :model-value="modelValue"
    content-class="suggestion-modal suggestion-modal-dialog"
    fullscreen
    scrollable
    :scrim="false"
    transition="dialog-bottom-transition"
    @update:model-value="emitUpdate"
  >
    <v-card rounded="0" class="dialog suggestion-modal">
      <v-card-title class="d-flex suggestion-modal__header">
        <v-btn
          class="ms-n2 me-2"
          icon
          size="24"
          variant="text"
          @click="closeModal"
        >
          <icon height="14" width="14">close-x</icon>
        </v-btn>
        <h4 class="flex-grow-1 suggestion-modal__header-title">Suggest Improvements</h4>
      </v-card-title>
      <v-card-text class="pt-5 suggestion-modal__content" style="height: calc(100vh - 64px);">
        <v-form @submit.prevent="submitForm">
          <v-textarea
            v-model="suggestion"
            class="mb-2 rounded-xl suggestion-textarea"
            label="What would you like to be able to do?"
            variant="outlined"
            rows="5"
            color="primary"
          ></v-textarea>

          <h5 class="font-weight-regular mt-1 mb-4 suggestion-modal__content-title">
            How important is this to you?
          </h5>
          <v-chip-group
            v-model="priority"
            class="px-0"
            mandatory
          >
            <v-chip
              v-for="level in importance"
              :key="level"
              :color="priority === level ? 'primary' : undefined"
              class="font-weight-bold suggestion-chip"
              rounded="pill"
              :variant="priority !== level ? 'outlined' : 'flat'"
              size="small suggestion-chip"
              :value="level"
            >
              {{ level }}
            </v-chip>
          </v-chip-group>
          <h5 class="mt-4 mb-4 font-weight-regular suggestion-modal__content-title">
            Can we contact you to hear more about this request, if needed?
          </h5>
          <v-radio-group v-model="contactPermission" class="suggestion-radio-group">
            <v-radio
              class="caption radio-off"
              color="primary"
              label="Yes"
              :value="1"
            />
            <v-radio
              class="caption radio-off"
              color="primary"
              label="No"
              :value="0"
            />
          </v-radio-group>
          <v-btn
            class="suggestion-modal__btn font-weight-bold text-decoration-none rounded-pill text-white"
            color="primary"
            :disabled="!suggestion || loading"
            size="large"
            :loading="loading"
            type="submit"
            variant="flat"
            width="100%"
          >
            Submit
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'close-modal', 'confirmation-modal'],
  data() {
    return {
      contactPermission: 1,
      importance: ['Nice to Have', 'Important', 'Critical'],
      loading: false,
      priority: 'Nice to Have',
      suggestion: ''
    };
  },
  computed: {
    ...mapState(['offer', 'partner', 'partnerUser'])
  },
  methods: {
    ...mapActions('partnerUser', ['setPartnerUser']),
    emitUpdate(value) {
      this.$emit('update:modelValue', value);
    },
    resetForm() {
      this.contactPermission = 1;
      this.loading = false;
      this.priority = 'Nice to Have';
      this.suggestion = '';
    },
    closeModal() {
      this.$emit('update:modelValue', false);
      this.$emit('close-modal');
    },
    async submitForm() {
      this.loading = true;

      if (this.partnerUser?.id) {
        this.setPartnerUser({ ...this.partnerUser, contact_permission: this.contactPermission });
      }
      await this.$api.misc.submitSuggestForm({
        suggestion: this.suggestion,
        contactPermission: this.contactPermission,
        priority: this.priority,
        partnerName: this.partner?.name,
        partnerId: this.partner?.id,
        partnerUserId: this.partnerUser?.id,
        offerId: this.offer?.id
      });

      this.resetForm();
      this.$emit('confirmation-modal', true);
      this.$emit('update:modelValue', false);
      this.$emit('close-modal', true);
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.suggestion-modal__content-title) {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.287rem;
  letter-spacing: 0.0071428571em;
  color: rgba(var(--v-theme-on-surface), 0.6) !important;
  margin-bottom: 13px !important;
}

.v-radio-group :deep(.v-input__details) {
  margin-top: -3px !important;
}

.success-modal {
  border-radius: 1rem;
}

:deep(.v-dialog--fullscreen) {
  box-shadow: none !important;
}

:deep(.suggestion-textarea .v-field-label) {
  margin-left: 12px !important;
  letter-spacing: 0em !important;
}

.suggestion-textarea {
  margin-bottom: 14px !important;
  :deep(.v-field) {
    border-radius: 16px !important;
  }

  :deep(.v-field__outline) {
    border-radius: 16px !important;
    color: rgba(var(--v-theme-on-surface), 1) !important;
  }

  :deep(.v-field--focused .v-field__outline) {
    color: rgba(var(--v-theme-primary), 1) !important;
  }

  :deep(.v-field--variant-outlined .v-field__outline__start) {
    flex: 0 0 16px !important;
    border-start-start-radius: 16px !important;
    border-end-start-radius: 16px !important;
    border-top-left-radius: 16px !important;
    border-bottom-left-radius: 16px !important;
  }

  :deep(.v-field--variant-outlined .v-field__outline__notch) {
    max-width: calc(100% - 32px) !important;
  }

  :deep(.v-field--variant-outlined .v-field__outline__end) {
    border-start-end-radius: 16px !important;
    border-end-end-radius: 16px !important;
    border-top-right-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
  }
}


:v-deep(.suggestion-textarea .v-field__input) {
  padding-top: 12px;
  padding-bottom: 18px !important;
  min-height: 120px !important;
}

.suggestion-chip {
  border-radius: 9999px;
}

.suggestion-radio-group {
  :deep(.v-selection-control__wrapper) {
    --v-selection-control-size: 20px;
    color: rgb(var(--v-theme-primary));
  }

  :deep(.v-selection-control__input) {
    height: 20px !important;
    width: 20px !important;
    min-height: 20px !important;
    min-width: 20px !important;
    border-radius: 50%;
    border: solid 2px currentColor;
    margin-right: 14px;
    margin-left: 10px;
    margin-top: -1px;
    color: inherit;

    & .v-icon {
      display: none;
    }

    & .v-selection-control__ripple {
      left: -1rem;
    }
  }

  :deep(.v-selection-control--dirty .v-selection-control__input) {
    background: currentColor !important;
    box-shadow: 0 0 0 3px rgb(var(--v-theme-surface, 255, 255, 255)) inset;
  }

  :deep(.v-selection-control) {
    margin-top: 4px !important;
    margin-left: 2px !important;
    margin-right: 2px !important;
  }

  :deep(.v-label) {
    color: rgba(var(--v-theme-on-surface), 0.6) !important;
    padding-left: 8px !important;
  }
}

:deep(.suggestion-modal__header) {
  min-height: 64px;
  padding: 16px 20px 10px !important;  
  align-items: center;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
}

.suggestion-modal__header-title {
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.0125em !important;
  line-height: 2rem;
}

.suggestion-modal__content {
  padding: 20px 24px 20px !important;
}

.suggestion-modal__btn {
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
  font-weight: 700 !important;
  min-height: 40px !important;
  height: 40px !important;
  text-transform: none;
  box-shadow: none !important;
}

.suggestion-modal__btn :deep(.v-btn__content) {
  color: rgb(var(--v-theme-on-primary));
}

.suggestion-chip {
  font-size: 0.74rem !important;
  font-weight: 700 !important;
  height: 24px !important;
  padding: 0 20px !important;
  letter-spacing: normal !important;
  border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
}

:deep(.suggestion-chip .v-chip__content) {
  line-height: 24px !important;
}

:deep(.suggestion-chip.v-chip--variant-outlined) {
  border-width: 1px !important;
}
</style>
